import { NgModule, Optional, SkipSelf } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { RouteReuseStrategy, RouterModule } from '@angular/router';

import { ShellComponent } from '@bo-client/core/shell/shell.component';
import { RouteReusableStrategy } from '@bo-client/core/route-reusable-strategy';
import { AuthenticationService } from '@bo-client/core/authentication/authentication.service';
import { HttpService } from '@bo-client/core/http/http.service';
import { Configuration, ConfigurationParameters } from '@bo-client/core/configuration';
import {
  UserService,
  TenantService,
  OrderRequestManagementService,
  DevelopmentAreasService,
  NotificationsService
} from '@bo-client/core/api/api';
import { ChangePasswordModalComponent } from '@bo-client/core/change-password-modal/change-password-modal.component';
import { AuthorizationInterceptor } from '@bo-client/core/authentication/authorization.interceptor';
import { TenantHeaderInterceptor } from '@bo-client/core/authentication/tenant-header.interceptor';
import { PriceCalculatorService } from './api/price-calculator.service';
import { MetaService } from './api/meta-service';
import { LogsService } from './api/logs.service';
import { FileUploadModule } from 'ng2-file-upload';
import { SharedModule } from '@bo-client/shared';
import { CompanyLocationsService } from './api/company-locations.service';
import { BusinessPartnersService } from './api/business-partners.service';
import { ActivitiesService } from './api/activities.service';
import { CURRENT_TENANT, URL_BASE_PATH } from '@shared/variables';
import { BehaviorSubject, Observable } from 'rxjs';
import { ShellExternalComponent } from '@bo-client/core/shell-tm/shell-external.component';
import { SideNavComponent } from '@bo-client/core/navigation/side-nav/side-nav.component';
import { ViewMenuComponent } from '@bo-client/core/navigation/view-menu/view-menu.component';
import { HeaderComponent } from '@bo-client/core/header/header.component';
import { RouterGuard } from '@bo-client/core/guards/router.guard';
import { WINDOW } from '@bo-client/core/provider/window.provider';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { TmPreventLoggedInAccessGuard } from '@bo-client/core/guards/tm-prevent-loggedin-access.guard';
import { FooterComponent } from '@bo-client/core/footer/footer.component';
import { FooterMenuComponent } from '@bo-client/core/navigation/footer-menu/footer-menu.component';
import { UserLoggedInGuard } from '@bo-client/core/guards/user-logged-in-guard.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { BuildUpToDateService } from '@bo-client/core/api/build-up-to-date.service';
import { MatTreeModule } from '@angular/material/tree';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { LandingPageComponent } from './landing-page/landing-page.component';

const apiConfigurationFactory = (authenticationService: AuthenticationService) => {
  let token = null;
  if (authenticationService.credentials) {
    token = authenticationService.credentials.token;
  }
  const params: ConfigurationParameters = {
    accessToken: token
  };
  return new Configuration(params);
};

const currentTenantSubjectFactory = (dataStore: DataShareService): Observable<TenantDto> => {
  return dataStore.currentTenant$;
};

@NgModule({
  imports: [HttpClientModule, SharedModule, RouterModule, FileUploadModule, MatTreeModule],
  declarations: [
    HeaderComponent,
    FooterComponent,
    ShellComponent,
    ShellExternalComponent,
    ChangePasswordModalComponent,
    SideNavComponent,
    ViewMenuComponent,
    FooterMenuComponent,
    LandingPageComponent
  ],
  exports: [],
  providers: [
    AuthenticationService,
    TmPreventLoggedInAccessGuard,
    UserLoggedInGuard,
    RouterGuard,
    HttpHapService,
    DataShareService,
    {
      provide: HttpClient,
      useClass: HttpService
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantHeaderInterceptor,
      multi: true
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy
    },
    {
      provide: Configuration,
      useFactory: apiConfigurationFactory,
      deps: [AuthenticationService]
    },
    TenantService,
    UserService,
    NotificationsService,
    AuthorizationService,
    OrderRequestManagementService,
    DevelopmentAreasService,
    LogsService,
    PriceCalculatorService,
    MetaService,
    CompanyLocationsService,
    BusinessPartnersService,
    BuildUpToDateService,
    ActivitiesService,
    {
      provide: CURRENT_TENANT,
      useFactory: currentTenantSubjectFactory,
      deps: [DataShareService]
    },
    {
      provide: WINDOW,
      useFactory: () => window
    },
    DataShareService
  ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule
  ) {
    // Import guard
    if (parentModule) {
      throw new Error(`${parentModule} has already been loaded. Import Core module in the AppModule only.`);
    }
  }
}
